// What the overview's preview cards show, derived from the record (#11426).
//
// Pure, so the rules worth pinning are pinned without a browser: how many rows
// a preview holds, what a row says, and — the one that matters most — when a
// component has nothing to say and must not render at all.

import { formatTemporal } from "@/utils/temporal";
import { humanizeEnumKey } from "../../Data/humanizeEnumKey";
import { isCellValueEmpty } from "../../Search/TablePanel/cellEmptiness";
import type {
  BackreferenceGroup,
  EntitySchema,
  Fact,
  ShallowEntity,
} from "../types";
import { factDisplayName, isArrayFact } from "../types";

/** Rows a preview shows before it defers to the tab. */
export const PREVIEW_LIMIT = 5;

/** Key facts the rail lists before it defers to the Facts tab. */
export const KEY_FACTS_LIMIT = 10;

export interface PreviewRow {
  id: string;
  primary: string;
  sub?: string;
  /** Right-hand slot: a date, already formatted for reading. */
  trailing?: string;
  /** A closed-vocabulary value, rendered as a tag. */
  tag?: string;
}

export interface RelationshipGroup {
  label: string;
  entities: ShallowEntity[];
  /** How many were not listed. */
  more: number;
}

/**
 * Whether the record actually holds this fact. The one definition of "held" —
 * the rail, the tab badge, the tab-presence map and the Facts legend all count
 * with it, so their numbers can never mean different things (rule 12). The
 * emptiness predicate itself is the grid's `isCellValueEmpty`, so a fact the
 * table renders as empty can never be counted as held here.
 */
export const hasFactValue = (fact: Fact): boolean => {
  return !isCellValueEmpty(
    isArrayFact(fact)
      ? fact.array
      : { scalar: fact.scalar, entity: fact.entity }
  );
};

/**
 * A fact the record detail computes from other rows rather than stores: the
 * system columns (Date Created, Created By, Last Updated At/By, the Last
 * Interaction family, #14680). It shows where someone named it (a fact card,
 * a chosen Key facts list) and nowhere else: Date Created is held on every
 * record, so counting it, or listing it unasked, would make every empty
 * record read as held and fill every unconfigured rail with "Created By:
 * System".
 */
export const isDerivedFact = (fact: Fact): boolean =>
  fact.provenance?.kind === "derived";

/** The record's own attributes: every fact that is not derived. */
export const storedFacts = (entity: EntitySchema): Fact[] =>
  entity.facts.filter((fact) => !isDerivedFact(fact));

/**
 * How many of the record's own attributes it holds: the N of "N of M facts
 * held", the Facts tab badge and the zero that dims the tab. One count, so
 * those surfaces can never disagree.
 */
export const heldFactCount = (entity: EntitySchema): number =>
  storedFacts(entity).filter(hasFactValue).length;

/**
 * Longest value the UNCONFIGURED rail will carry. Past this the fact is prose,
 * and a paragraph nobody chose for a 320px rail belongs on the Facts tab
 * where it has the width to be read; the denominator says how many were left
 * there. A fact the organization chose for the card is not gated: it shows
 * whole, wrapped in its row (#13369).
 */
const RAIL_VALUE_MAX_CHARS = 60;

/** Facts whose values are paragraphs by declaration rather than by length. */
const PROSE_UNITS = new Set(["multiline"]);

/** A value that is one web address. It is never prose, however long: the
 *  rail's link renderer shortens it to fit its column (#12010), so the
 *  length gate below does not apply. */
const isUrl = (value: string): boolean => /^https?:\/\/\S+$/.test(value.trim());

/**
 * A link set: an array fact whose values are entity references. It is a
 * relationship wearing a fact's clothes, and the rail's Key facts card is
 * scalar kv rows only (keeper ruling A1, round 4) — a person with 234
 * coverage links was rendering every one of them in the rail. Link sets
 * belong to the Relationships card, which groups them by fact label and
 * caps each group. A *single* entity reference stays a key fact: the mock's
 * rail opens with the record's Manager.
 */
export const isLinkSet = (fact: Fact): boolean =>
  isArrayFact(fact) && fact.array.some((v) => v.entity !== null);

/**
 * Whether a fact's value is rail-sized rather than prose. The identity strip reads it too: prose
 * is never a stat card and stays in the Key facts card (#13370, round 17 D).
 */
export const isRailSized = (fact: Fact): boolean => {
  if (PROSE_UNITS.has(fact.scalar_unit ?? "")) return false;
  if (isLinkSet(fact)) return false;
  if (isArrayFact(fact)) return true;
  const scalar = fact.scalar;
  if (typeof scalar === "string") {
    return isUrl(scalar) || scalar.length <= RAIL_VALUE_MAX_CHARS;
  }
  return true;
};

/**
 * The facts the Key facts card lists. With a chosen list (a layout's Key
 * facts, else the org's pins) it is exactly those the record holds, in the
 * chosen order, whatever their length, and nothing else.
 *
 * An org that has configured nothing still gets a useful rail — the record's
 * populated, rail-sized facts in schema order — rather than an empty card.
 */
export function keyFacts(
  entity: EntitySchema,
  pinned: string[] | undefined,
  exclude: string[] = []
): { facts: Fact[]; total: number } {
  const skip = new Set(exclude);
  // "N of M facts held" means N STATED of M DEFINED — the same sentence the
  // Facts tab prints, so the two surfaces cannot mean different things by
  // the same five words (keeper ruling, all-kinds sweep). Using the HELD
  // count as the denominator made a person holding four of forty-nine facts
  // read "3 of 4 facts held", which claims one of its four is unheld.
  const populated = entity.facts.filter(hasFactValue);
  // M counts the record's own attributes: a derived fact is defined on every
  // record and says nothing about how complete this one is (#14680).
  const total = storedFacts(entity).length;

  // A configured list is a CURATED list, so it is the whole rail rather than
  // just the top of it. Falling through to schema order after the pinned
  // fields is how the rail filled up with an org's dead columns — the point of
  // the rail is that someone chose what belongs on it. For the same reason the
  // size gate does not apply to it: someone chose that paragraph for the card,
  // and dropping it in silence left the card without it, or with no card at
  // all (#13369). A link set still belongs to the Relationships card.
  // A list the page already states in full (its facts all stat cards, round
  // 42) is still a curated list: the card lists none of it, never the
  // schema's other facts in its place.
  if (pinned && pinned.length > 0) {
    const chosen = pinned.filter((name) => !skip.has(name));
    const byName = new Map(
      populated.filter((f) => !isLinkSet(f)).map((f) => [f.name, f])
    );
    const facts = chosen
      .map((name) => byName.get(name))
      .filter((f): f is Fact => Boolean(f));
    return {
      facts: facts.slice(0, KEY_FACTS_LIMIT),
      total,
    };
  }

  // Unconfigured, the rail is still better than nothing: the record's own
  // populated attributes, in schema order, with prose left to the Facts tab.
  // A derived fact is listed only where someone chose it (#14680).
  const railable = populated.filter(
    (f) => !isDerivedFact(f) && isRailSized(f) && !skip.has(f.name)
  );
  return {
    facts: railable.slice(0, KEY_FACTS_LIMIT),
    total,
  };
}

/**
 * The record's links, grouped by what the link means.
 *
 * Groups holding nothing are dropped rather than rendered as an empty heading:
 * a rail full of headings with no rows under them is the shape the current
 * product takes on a sparse record, and it is what makes the page feel broken.
 */
export function relationshipGroups(
  facts: Fact[],
  backreferences: BackreferenceGroup[],
  perGroup = 3,
  // Capped by RENDERED ROWS, not by group count (#11426, keeper ruling on the
  // Facts badge against the rail count).
  //
  // A three-GROUP cap dropped a held fact off the page entirely whenever a
  // record held four link facts — no row, no count, nothing saying it existed
  // — so the rail stated three where the tab badge counted four. A row budget
  // spends the same vertical space but lets a card carry more groups when its
  // groups are short, which is exactly when there is room for them.
  maxRows = 12,
  // Records another card on the same rail already rendered (the Key facts
  // Manager row) — rule 15: the same rail never states the same link twice
  // in adjacent cards. The Relationships TAB keeps them; completeness is
  // that surface's job.
  excludeIds: string[] = []
): RelationshipGroup[] {
  // Entity-valued facts lead — the mock's rail opens with the record's
  // Manager, which is a fact link, and reading backreferences alone dropped
  // exactly those rows (the same defect class the tab count had, #11426).
  // One record shows once: an entity already listed under an earlier group
  // is skipped, so the rail's rows can never outnumber the denominator's
  // deduplicated total.
  const seen = new Set<string>(excludeIds);
  const groups: RelationshipGroup[] = [];
  const push = (label: string, all: ShallowEntity[]): void => {
    const fresh = all.filter((e) => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      return true;
    });
    if (fresh.length === 0) return;
    groups.push({
      label,
      entities: fresh.slice(0, perGroup),
      more: Math.max(0, fresh.length - perGroup),
    });
  };

  for (const fact of facts) {
    if (isArrayFact(fact)) {
      push(
        factDisplayName(fact),
        fact.array
          .map((v) => v.entity)
          .filter((e): e is ShallowEntity => Boolean(e))
      );
    } else if ("entity" in fact && fact.entity) {
      push(factDisplayName(fact), [fact.entity]);
    }
  }
  for (const group of backreferences) {
    push(
      backreferenceLabel(group),
      group.entities
        .map((e) => e.entity)
        .filter((e): e is ShallowEntity => Boolean(e))
    );
  }
  // Fill until the budget is spent; a group that would overrun it stops the
  // card rather than being half-rendered.
  const within: RelationshipGroup[] = [];
  let rows = 0;
  for (const group of groups) {
    if (rows + group.entities.length > maxRows) break;
    within.push(group);
    rows += group.entities.length;
  }
  // Never render nothing when there is something: a single group larger than
  // the whole budget still gets its (already per-group capped) rows.
  return within.length > 0 ? within : groups.slice(0, 1);
}

/**
 * The sentinel the backend writes when a relationship has no inverse name.
 * It is a marker, not a label, and printing it puts "INVERSE RELNAME UNSET"
 * on the page.
 */
const NO_INVERSE_NAME = "INVERSE RELNAME UNSET";

/**
 * What a group with neither name available is called.
 *
 * A backreference always means at least this much — these records point at
 * this one — so it is a true statement rather than a placeholder, which is
 * what lets it stand as a heading at all (rule 18).
 */
const UNNAMED_RELATIONSHIP = "Related";

/**
 * What to call a group of links: its inverse name, else the forward one,
 * else the generic truth.
 *
 * The sentinel is filtered from BOTH sides. It used to be filtered only from
 * the inverse name, on the assumption that the forward name is always set —
 * and nothing enforced that assumption, so a group with neither would have
 * printed the marker as a heading. Worth enforcing rather than observing
 * (keeper guard, all-kinds sweep): in the org this was swept against, EVERY
 * backreference group arrives with the sentinel as its inverse name, so the
 * fallback is the common path rather than the rare one.
 */
export function backreferenceLabel(group: BackreferenceGroup): string {
  const usable = (name: string | null | undefined): string | null => {
    const trimmed = (name ?? "").trim();
    return trimmed && trimmed !== NO_INVERSE_NAME ? trimmed : null;
  };
  return (
    usable(group.name) ?? usable(group.forward_name) ?? UNNAMED_RELATIONSHIP
  );
}

/* The rail's denominator is `relationshipCount` (../Tabs/relationshipCount):
   one deduplicated set shared with the tab and the strip count, so the three
   numbers can never disagree. */

export interface InteractionLike {
  id: string;
  title: string;
  meeting_type: string;
  occurrence: Parameters<typeof formatTemporal>[0];
  primary_participants: ShallowEntity[];
}

/**
 * An interaction row: what it was, who was in it, when.
 *
 * Participants are the sub-line rather than a column because they are what
 * tells you what a meeting was when its title is "Follow-up call".
 */
export function interactionRows(
  interactions: InteractionLike[],
  limit = PREVIEW_LIMIT,
  // Register rule 17: a record never names itself. The record's own entry in
  // the participants list is dropped; a line that empties does not render.
  selfId?: string
): PreviewRow[] {
  return interactions.slice(0, limit).map((i) => {
    const others = i.primary_participants.filter(
      (p) => !selfId || p.id !== selfId
    );
    const sub = others
      .map((p) => p.display_name || p.name)
      .filter(Boolean)
      .join(", ");
    return {
      id: i.id,
      primary: i.title,
      sub: sub || undefined,
      tag: i.meeting_type || undefined,
      trailing: i.occurrence
        ? formatTemporal(i.occurrence, "medium")
        : undefined,
    };
  });
}

export interface DocumentLike {
  id: string;
  filename: string;
  document_types?: string[] | null;
  document_date?: string | null;
}

/**
 * A document row: its name, what kind it is, and the date it states.
 *
 * Only the document's OWN date, and nothing when it states none. It used to
 * declare `uploaded_at` as well and describe a fallback to it, but the code
 * never read the field — and the fallback would have been wrong anyway: a
 * business date and an upload instant are different kinds of time, and this
 * row has one bare slot with no room to say which it is showing. A date
 * renders with its kind or it does not render (keeper ruling, all-kinds
 * sweep), so an unlabelled slot shows the one kind the reader can assume.
 */
export function documentRows(
  documents: DocumentLike[],
  limit = PREVIEW_LIMIT,
  typeLabels?: Record<string, string>
): PreviewRow[] {
  // The chip names the type in the org's own words. `document_types` holds
  // catalog KEYS ("investor_presentation"), and the raw key reached the chip
  // verbatim (#11426); the catalog's display name wins, and a key the catalog
  // doesn't hold still humanizes rather than showing its underscores.
  const label = (key: string): string =>
    typeLabels?.[key] ?? humanizeEnumKey(key);
  return documents.slice(0, limit).map((d) => ({
    id: d.id,
    primary: d.filename,
    tag: d.document_types?.[0] ? label(d.document_types[0]) : undefined,
    trailing: d.document_date
      ? formatTemporal({ granularity: "date", day: d.document_date }, "medium")
      : undefined,
  }));
}

/** The label a fact shows in the rail. */
export const railFactLabel = (fact: Fact): string => factDisplayName(fact);
